// src/components/Login.tsx
import { useState, type FormEvent } from 'react';
import { useBranding, DEFAULT_LOGO_URL } from '../hooks/useBranding';
import { useAuth } from '../hooks/useAuth';
import { Button } from './ui/Button';
import { CodeEntry } from '../features/two-step/CodeEntry';
import { SetupPanel } from '../features/two-step/SetupPanel';
import { RecoveryCodesPanel } from '../features/two-step/RecoveryCodesPanel';
import { verifySignIn, type SessionResult } from '../features/two-step/api';

// After a correct password: ask for the two-step code, or set two-step up
// first when the user's role requires it (Phase 1 quotation G20).
type Step =
  | { kind: 'password' }
  | { kind: 'verify'; mfaToken: string }
  | { kind: 'setup'; mfaToken: string }
  | { kind: 'codes'; codes: string[]; session: SessionResult };

export default function Login() {
  const { branding } = useBranding();
  const { login, completeSignIn } = useAuth();
  const [step, setStep] = useState<Step>({ kind: 'password' });
  const [codeError, setCodeError] = useState<string | null>(null);
  const backToPassword = (message?: string) => { setStep({ kind: 'password' }); setPassword(''); setError(message || ''); };
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true); setError('');
    try {
      const outcome = await login(username.trim(), password);
      if (outcome.stage !== 'session') { setCodeError(null); setStep({ kind: outcome.stage, mfaToken: outcome.mfaToken }); }
    } catch(e) { setError(e instanceof Error ? e.message : String(e)); }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      // Derived from the two configured brand colors (Admin > Branding), not
      // hardcoded — a flat navy no longer reflects a hospital's actual theme
      // once they've picked their own primary/accent colors.
      background: 'linear-gradient(160deg, var(--teal, #0B5FD6), var(--accent, #00D9A5))',
      padding: 16, boxSizing: 'border-box',
    }}>
      {/* Fixed 380px overflowed narrow phones (e.g. a 375px-wide viewport left
          no room for the 16px page padding, clipping the card's edge) —
          capped at 380 but always shrinks to fit the viewport instead. */}
      <div className="card fade-in" style={{ width: '100%', maxWidth: step.kind === 'setup' || step.kind === 'codes' ? 440 : 380, padding: 32, boxSizing: 'border-box' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {branding.logo_url
            ? <img src={branding.logo_url} alt="logo" style={{ height: 40, objectFit: 'contain', marginBottom: 10 }} />
            : <img src={DEFAULT_LOGO_URL} alt="ClintoBytes" style={{ height: 44, objectFit: 'contain', marginBottom: 10 }} />}
          <div style={{ fontSize: 18, fontWeight: 700 }}>{branding.hospital_name || 'HMS'}</div>
          {branding.tagline && <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 2 }}>{branding.tagline}</div>}
        </div>

        {step.kind === 'verify' && (
          <div>
            <div className="mb-3 text-center text-[15px] font-semibold text-ink">Two-step login</div>
            <CodeEntry
              pending={loading} error={codeError} submitLabel="Sign in"
              onSubmit={async (proof) => {
                setLoading(true); setCodeError(null);
                try {
                  const r = await verifySignIn(step.mfaToken, proof);
                  completeSignIn(r.token, r.user);
                } catch (e) {
                  const msg = e instanceof Error ? e.message : String(e);
                  // An expired half-way sign-in (10 min) or a lockout starts over.
                  if (/expired|locked|Too many/i.test(msg)) backToPassword(msg); else setCodeError(msg);
                }
                setLoading(false);
              }}
            />
            <Button type="button" variant="link" className="mt-3 w-full text-xs text-ink-muted" onClick={() => backToPassword()}>← Use a different account</Button>
          </div>
        )}

        {step.kind === 'setup' && (
          <div>
            <div className="mb-1 text-center text-[15px] font-semibold text-ink">Set up two-step login</div>
            <div className="mb-3 text-center text-xs text-ink-muted">Your role requires it. This takes about a minute.</div>
            <SetupPanel mfaToken={step.mfaToken} onEnabled={(r) => {
              if (r.token && r.user) setStep({ kind: 'codes', codes: r.recovery_codes, session: { token: r.token, user: r.user } });
              else backToPassword('Two-step login is on. Please sign in again.');
            }} />
            <Button type="button" variant="link" className="mt-3 w-full text-xs text-ink-muted" onClick={() => backToPassword()}>← Cancel</Button>
          </div>
        )}

        {step.kind === 'codes' && <div className="mb-3 text-center text-[15px] font-semibold text-ink">Save your recovery codes</div>}
        {step.kind === 'codes' && (
          <RecoveryCodesPanel codes={step.codes} account={username.trim()} doneLabel="Continue to the app"
            onDone={() => completeSignIn(step.session.token, step.session.user)} />
        )}

        {step.kind === 'password' && <form onSubmit={submit}>
          <div style={{ marginBottom: 14 }}>
            <label className="lbl">Username</label>
            <input value={username} onChange={e => setUsername(e.target.value)} autoFocus placeholder="e.g. admin" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="lbl">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
          </div>

          {error && (
            <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 'var(--radius)', color: '#991B1B', fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={loading || !username.trim() || !password}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>}
      </div>
    </div>
  );
}
